import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { UseMutateAsyncFunction } from "react-query";
import { toast } from "react-toastify";

import { faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { FieldType } from "../../constants";
import { DataFormFieldProvider } from "../../contexts/dataFormFieldContext";
import { CustomDataDto } from "../../types/dto";
import { ModalSetting } from "../../types/setting";
import { DataForm, setDataFormField } from "../dataForm/dataForm";
import { FieldDropdown } from "../dataForm/fieldDropdown";
import { FieldTextArea } from "../dataForm/fieldTextArea";
import { FormControlWrapper } from "../dataForm/formControlWrapper";
import { RibbonButtons } from "../dataTable/dataTableRibbonButtons";
import {
	datedButtonSetting,
	datedIconSetting,
	datedMessageSetting,
	enforceMinVerSetting,
} from "./customDataModalSettings";

enum CustomDataType {
	Arbitrary = "Arbitrary",
	DatedMessage = "DatedMessage",
	EnforceMinimumVersion = "EnforceMinimumVersion",
	DatedIcon = "DatedIcon",
	DatedButton = "DatedButton",
}

const customDataTypeDropdownValues: { id: string; label: string }[] = [];
for (const t in CustomDataType) {
	customDataTypeDropdownValues.push({ id: t, label: t });
}

// Derive the editor's initial view (parsed object, pretty JSON string, and
// resolved type) from a custom-data item. Pure and synchronous so it can seed
// useState directly: the arbitrary-JSON textarea is uncontrolled and captures
// its defaultValue at mount, so the content must exist on the first render, not
// arrive later from an effect.
const seedFromCustomData = (
	custom: any,
): {
	dataObject: any;
	dataObjectString: string;
	customDataType: CustomDataType;
} => {
	const dataStr = custom?.data;
	if (!dataStr) {
		return {
			dataObject: {},
			dataObjectString: "{}",
			customDataType: CustomDataType.Arbitrary,
		};
	}
	try {
		const obj = JSON.parse(dataStr);
		const isKnownType = Object.values(CustomDataType).includes(obj.type);
		return {
			dataObject: obj,
			dataObjectString: JSON.stringify(obj, null, 2),
			customDataType: isKnownType ? obj.type : CustomDataType.Arbitrary,
		};
	} catch {
		return {
			dataObject: {},
			dataObjectString: "{}",
			customDataType: CustomDataType.Arbitrary,
		};
	}
};

// Identity of a custom-data item for seeding purposes: id + stored data. A bare
// object-reference change with the same id and data (e.g. a react-query refetch)
// is not a real change and must not trigger a re-seed.
const seedKey = (custom: any): string =>
	`${custom?.id ?? ""}::${custom?.data ?? ""}`;

// The field names each structured type owns, derived from its editor setting so
// they can't drift. Arbitrary has no entry (it is free-form).
const settingFieldNames = (setting: {
	fields: { field: string }[][];
}): string[] => setting.fields.flat().map((f) => f.field);

const knownTypeFields: Record<string, string[]> = {
	[CustomDataType.DatedMessage]: settingFieldNames(datedMessageSetting),
	[CustomDataType.EnforceMinimumVersion]:
		settingFieldNames(enforceMinVerSetting),
	[CustomDataType.DatedIcon]: settingFieldNames(datedIconSetting),
	[CustomDataType.DatedButton]: settingFieldNames(datedButtonSetting),
};

// Keep only the keys a type owns, so switching type can't carry a previous
// type's (or an arbitrary item's) fields into the new one.
const pruneToKnownFields = (obj: any, fields: string[]): any => {
	const out: any = {};
	for (const key of fields) if (obj && key in obj) out[key] = obj[key];
	return out;
};

export const CustomDataModal = ({
	initialCustomData,
	isModalOpen,
	setIsModalOpen,
	submitEditCustomData,
	submitDeleteCustomData,
}: {
	initialCustomData: any;
	isModalOpen: boolean;
	setIsModalOpen: (isOpen: boolean) => void;
	submitEditCustomData: UseMutateAsyncFunction<
		any,
		unknown,
		CustomDataDto,
		unknown
	>;
	submitDeleteCustomData: UseMutateAsyncFunction<
		any,
		unknown,
		CustomDataDto,
		unknown
	>;
}) => {
	const [metaData, setMetadata] = useState<any>(initialCustomData);
	// Seed synchronously from the initial item so the uncontrolled textarea has
	// its content on first render (see seedFromCustomData). Computed once via a
	// lazy initialiser and shared across the three fields below.
	const [initialSeed] = useState(() => seedFromCustomData(initialCustomData));
	const [dataObject, setDataObject] = useState<any>(initialSeed.dataObject);
	const [dataObjectString, setDataObjectString] = useState<any>(
		initialSeed.dataObjectString,
	);

	const setNiceDataObjStr = (jsonStr: string) => {
		try {
			const obj = JSON.parse(jsonStr);
			setDataObjectString(JSON.stringify(obj, null, 2));
		} catch {
			setDataObjectString(jsonStr);
		}
	};

	const [customDataType, setCustomDataType] = useState<any>(
		initialSeed.customDataType,
	);
	const [arbitraryJsonError, setArbitraryJsonError] = useState<string | null>(
		null,
	);

	// Tracks the item last seeded into local state. Starts undefined so the first
	// effect run (re)seeds and surfaces any parse error on open.
	const seededKeyRef = useRef<string | undefined>(undefined);

	// Re-seed when the item actually changes (the parent keys the modal on id, so
	// a different item usually remounts and the lazy initialisers above handle
	// it; this covers an in-place prop change too). Gated on id + stored data so a
	// bare object-reference change for the same item — e.g. react-query handing
	// back a fresh object after an onSettled refetch — does NOT wipe in-progress
	// edits.
	useEffect(() => {
		const key = seedKey(initialCustomData);
		if (key === seededKeyRef.current) return;
		seededKeyRef.current = key;

		const seed = seedFromCustomData(initialCustomData);
		setMetadata(initialCustomData);
		setDataObject(seed.dataObject);
		setDataObjectString(seed.dataObjectString);
		setCustomDataType(seed.customDataType);
		setArbitraryJsonError(null);

		// seedFromCustomData falls back to an empty Arbitrary editor on a parse
		// failure; surface that to the user here (the pure helper stays silent).
		const newDataStr = initialCustomData?.data;
		if (newDataStr) {
			try {
				JSON.parse(newDataStr);
			} catch (e: any) {
				console.error(`Json: ${newDataStr}\n`, e);
				toast.error("Failed to parse custom data JSON");
			}
		}
	}, [initialCustomData]);

	const setDataObjectFromTextArea = (value: string) => {
		setNiceDataObjStr(value);

		try {
			const obj = JSON.parse(value);
			setDataObject(obj);
			setArbitraryJsonError(null);
		} catch (e: any) {
			// Invalid JSON is an expected user state; surface it via the field
			// error rather than logging the parse exception to the console.
			setArbitraryJsonError(e?.message);
		}
	};

	const closeModal = () => {
		// Only close. The seeding effect owns (re)populating state from
		// initialCustomData on open; wiping here strands a reopened item on
		// empty when the parent re-selects the same object reference.
		setArbitraryJsonError(null);
		setIsModalOpen(false);
	};

	const metadataSetting: ModalSetting = {
		fields: [
			[
				{
					field: "id",
					isMandatory: true,
					isDisabled: true,
					title: "Id",
				},
			],
			[
				{
					field: "name",
					isMandatory: true,
					title: "Name",
					placeholder: "Enter Name",
				},
			],
			[
				{
					field: "description",
					isMandatory: false,
					title: "Description",
					placeholder: "Enter Description",
				},
			],
			[
				{
					field: "executionPriority",
					type: FieldType.NUMBER,
					isMandatory: true,
					title: "Execution Priority",
					placeholder: "Enter Execution Priority",
				},
			],
		],
	};

	const setDataObjectField = (field: any) => (value: any) => {
		// Functional update: several JSON_DICT fields seed their initialData via
		// mount effects that can fire in one commit, each with its own
		// setDataObjectField bound to the same render's dataObject. Reading that
		// closure would make the concurrent seeds merge onto a stale snapshot and
		// resurrect pruned/foreign fields; merging onto `old` keeps every update.
		// dataObjectString isn't displayed while a structured editor is shown
		// (the preview reads dataObject; the arbitrary textarea is resynced by
		// handleTypeChange on switch), so it isn't updated here.
		setDataObject((old: any) => ({ ...old, [field]: value }));
	};

	const handleTypeChange = (newType: any) => {
		setCustomDataType(newType);
		// Clear the arbitrary parse error: switching away abandons the (possibly
		// unparsed) arbitrary text buffer.
		setArbitraryJsonError(null);

		// Prune to the new type's own fields so the previous type's — or an
		// arbitrary item's — fields aren't carried in and saved as stale/leaked
		// data. Arbitrary is free-form, so it keeps whatever is there for
		// hand-editing. Shared fields (e.g. start/end date) survive, so a
		// mis-click is recoverable. This runs only on a user type change, never
		// on seeding an existing item.
		const knownFields = knownTypeFields[newType];
		const nextObj = knownFields
			? pruneToKnownFields(dataObject, knownFields)
			: dataObject;
		setDataObject(nextObj);
		setDataObjectString(JSON.stringify(nextObj, null, 2));
	};

	const changeableDataFields = useMemo(() => {
		switch (customDataType) {
			case CustomDataType.DatedMessage:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={datedMessageSetting}
					/>
				);
			case CustomDataType.EnforceMinimumVersion:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={enforceMinVerSetting}
					/>
				);
			case CustomDataType.DatedIcon:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={datedIconSetting}
					/>
				);
			case CustomDataType.DatedButton:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={datedButtonSetting}
					/>
				);
			case CustomDataType.Arbitrary:
			default:
				return (
					<DataFormFieldProvider
						fieldSetting={{ field: "", title: "Arbitrary JSON" }}
					>
						<FieldTextArea
							isForced={false}
							error={arbitraryJsonError}
							setValue={setDataObjectFromTextArea}
							fieldData={dataObjectString}
							autoFocus={false}
							isDisabled={false}
							maxLength={undefined}
							tooltip={undefined}
						/>
					</DataFormFieldProvider>
				);
		}
	}, [customDataType, arbitraryJsonError, dataObject, dataObjectString]);

	const getCompleteCustomData = () => {
		const completeCustomData =
			customDataType === CustomDataType.Arbitrary
				? {
						...dataObject,
					}
				: {
						type: customDataType,
						...dataObject,
					};
		return completeCustomData;
	};

	return (
		<Modal
			as={Modal.Dialog}
			centered
			show={isModalOpen}
			onHide={closeModal}
			className="modal-lg"
		>
			<Modal.Header>
				<Modal.Title>
					Edit Custom Data: {metaData?.id}
					{metaData?.name ? ` - ${metaData?.name}` : ""}
				</Modal.Title>
				<RibbonButtons>
					<Button
						onClick={closeModal}
						variant="outline-primary"
						size="sm"
						aria-label="Close"
					>
						<FontAwesomeIcon icon={faX} />
					</Button>
				</RibbonButtons>
			</Modal.Header>
			<Modal.Body>
				<DataForm
					currentFormData={metaData}
					setFormData={setDataFormField(setMetadata)}
					editorSetting={metadataSetting}
				/>
				<DataFormFieldProvider
					fieldSetting={{ field: "", title: "Type" }}
				>
					<FieldDropdown
						isForced={true}
						dropdownData={customDataTypeDropdownValues}
						isMandatory={true}
						fieldData={customDataType}
						setValue={handleTypeChange}
						// mandatory to avoid ts errors
						autoFocus={false}
						error={undefined}
						isDisabled={false}
						tooltip={undefined}
					/>
				</DataFormFieldProvider>
				{changeableDataFields}
				<DataFormFieldProvider
					fieldSetting={{ field: "", title: "JSON" }}
				>
					<FormControlWrapper data-testid="test-fieldTextArea">
						<pre data-testid="json-preview">
							{JSON.stringify(
								getCompleteCustomData(),
								undefined,
								2,
							)}
						</pre>
					</FormControlWrapper>
				</DataFormFieldProvider>
			</Modal.Body>
			<Modal.Footer>
				<Button
					className="ms-auto"
					variant="secondary"
					onClick={() => {
						if (metaData) submitDeleteCustomData(metaData);
						closeModal();
					}}
				>
					Delete
				</Button>
				<Button
					disabled={
						// Block save while the arbitrary editor holds invalid
						// JSON. The type-switch bypass is closed at the source, not
						// here: handleTypeChange clears this error and prunes
						// dataObject on a type change, so a stale error can't
						// linger and foreign fields can't leak.
						customDataType === CustomDataType.Arbitrary &&
						arbitraryJsonError !== null
					}
					variant="primary"
					onClick={() => {
						const completeCustomData = {
							...metaData,
							data: JSON.stringify(getCompleteCustomData()),
						};
						submitEditCustomData(completeCustomData);
						closeModal();
					}}
				>
					Save
				</Button>
			</Modal.Footer>
		</Modal>
	);
};
